/**
 * Common airline names and hotel brands, so a booking typed as "Crowne Plaza AiRE" is
 * counted under IHG in stats. Longer names come first so "Holiday Inn Express" wins over
 * "Holiday Inn".
 */

export const AIRLINES: Record<string, string> = {
  AA: 'American',
  AC: 'Air Canada',
  AF: 'Air France',
  AM: 'Aeromexico',
  AS: 'Alaska',
  AV: 'Avianca',
  AY: 'Finnair',
  AZ: 'ITA Airways',
  B6: 'JetBlue',
  BA: 'British Airways',
  CM: 'Copa',
  CX: 'Cathay Pacific',
  DL: 'Delta',
  EI: 'Aer Lingus',
  EK: 'Emirates',
  EY: 'Etihad',
  F9: 'Frontier',
  G4: 'Allegiant',
  HA: 'Hawaiian',
  IB: 'Iberia',
  JL: 'Japan Airlines',
  KL: 'KLM',
  LA: 'LATAM',
  LH: 'Lufthansa',
  LX: 'Swiss',
  MX: 'Breeze',
  NH: 'ANA',
  NK: 'Spirit',
  OS: 'Austrian',
  QF: 'Qantas',
  QR: 'Qatar Airways',
  SK: 'SAS',
  SQ: 'Singapore Airlines',
  SY: 'Sun Country',
  TK: 'Turkish Airlines',
  TP: 'TAP Air Portugal',
  UA: 'United',
  VS: 'Virgin Atlantic',
  WN: 'Southwest',
  WS: 'WestJet',
};

export function airlineName(code: string): string | null {
  return AIRLINES[code.trim().toUpperCase()] ?? null;
}

const BRANDS: [brand: string, program: string][] = [
  ['Holiday Inn Express', 'IHG One Rewards'],
  ['Holiday Inn', 'IHG One Rewards'],
  ['Crowne Plaza', 'IHG One Rewards'],
  ['InterContinental', 'IHG One Rewards'],
  ['Kimpton', 'IHG One Rewards'],
  ['Hotel Indigo', 'IHG One Rewards'],
  ['voco', 'IHG One Rewards'],
  ['Staybridge Suites', 'IHG One Rewards'],
  ['Candlewood Suites', 'IHG One Rewards'],
  ['avid hotel', 'IHG One Rewards'],
  ['EVEN Hotel', 'IHG One Rewards'],
  ['Six Senses', 'IHG One Rewards'],
  ['Regent', 'IHG One Rewards'],
  ['JW Marriott', 'Marriott Bonvoy'],
  ['Ritz-Carlton', 'Marriott Bonvoy'],
  ['St. Regis', 'Marriott Bonvoy'],
  ['Westin', 'Marriott Bonvoy'],
  ['Sheraton', 'Marriott Bonvoy'],
  ['Le Méridien', 'Marriott Bonvoy'],
  ['Renaissance', 'Marriott Bonvoy'],
  ['Courtyard', 'Marriott Bonvoy'],
  ['Residence Inn', 'Marriott Bonvoy'],
  ['SpringHill Suites', 'Marriott Bonvoy'],
  ['Fairfield', 'Marriott Bonvoy'],
  ['TownePlace Suites', 'Marriott Bonvoy'],
  ['AC Hotel', 'Marriott Bonvoy'],
  ['Aloft', 'Marriott Bonvoy'],
  ['Moxy', 'Marriott Bonvoy'],
  ['Four Points', 'Marriott Bonvoy'],
  ['Autograph Collection', 'Marriott Bonvoy'],
  ['Tribute Portfolio', 'Marriott Bonvoy'],
  ['Delta Hotels', 'Marriott Bonvoy'],
  ['Element', 'Marriott Bonvoy'],
  ['Gaylord', 'Marriott Bonvoy'],
  ['W Hotel', 'Marriott Bonvoy'],
  ['Marriott', 'Marriott Bonvoy'],
  ['Waldorf Astoria', 'Hilton Honors'],
  ['Conrad', 'Hilton Honors'],
  ['Canopy by Hilton', 'Hilton Honors'],
  ['Curio Collection', 'Hilton Honors'],
  ['DoubleTree', 'Hilton Honors'],
  ['Embassy Suites', 'Hilton Honors'],
  ['Hilton Garden Inn', 'Hilton Honors'],
  ['Hampton', 'Hilton Honors'],
  ['Homewood Suites', 'Hilton Honors'],
  ['Home2 Suites', 'Hilton Honors'],
  ['Tru by Hilton', 'Hilton Honors'],
  ['Tapestry Collection', 'Hilton Honors'],
  ['Hilton', 'Hilton Honors'],
  ['Park Hyatt', 'World of Hyatt'],
  ['Grand Hyatt', 'World of Hyatt'],
  ['Hyatt Regency', 'World of Hyatt'],
  ['Hyatt Centric', 'World of Hyatt'],
  ['Hyatt Place', 'World of Hyatt'],
  ['Hyatt House', 'World of Hyatt'],
  ['Andaz', 'World of Hyatt'],
  ['Thompson', 'World of Hyatt'],
  ['Alila', 'World of Hyatt'],
  ['Hyatt', 'World of Hyatt'],
  ['Wyndham', 'Wyndham Rewards'],
  ['La Quinta', 'Wyndham Rewards'],
  ['Days Inn', 'Wyndham Rewards'],
  ['Comfort Inn', 'Choice Privileges'],
  ['Comfort Suites', 'Choice Privileges'],
  ['Cambria', 'Choice Privileges'],
  ['Quality Inn', 'Choice Privileges'],
  ['Best Western', 'Best Western Rewards'],
  ['Fairmont', 'ALL Accor'],
  ['Raffles', 'ALL Accor'],
  ['Sofitel', 'ALL Accor'],
  ['Pullman', 'ALL Accor'],
  ['Novotel', 'ALL Accor'],
  ['Mercure', 'ALL Accor'],
  ['ibis', 'ALL Accor'],
  ['Omni', 'Omni Select Guest'],
];

/** Brand and loyalty program for a hotel name, if it's a known chain. */
export function hotelBrand(name: string): { brand: string; program: string } | null {
  const n = name.toLowerCase();
  for (const [brand, program] of BRANDS) {
    if (n.includes(brand.toLowerCase())) return { brand, program };
  }
  return null;
}

/** Chains where my status gets a 4 PM checkout; everywhere else it's the usual 11 AM. */
const LATE_CHECKOUT: Record<string, string> = {
  'Marriott Bonvoy': '16:00',
  'World of Hyatt': '16:00',
};

/** Local checkout time (HH:MM) to assume for a stay at this hotel. */
export function defaultCheckoutTime(hotelName: string): string {
  const b = hotelBrand(hotelName);
  return (b && LATE_CHECKOUT[b.program]) ?? '11:00';
}
